/**
 * #594 — what the order ticket's quick-size chip strip actually offers.
 *
 * The stored presets (`displaySettings.quickSizes`) are a trader-set list; the
 * chip strip is a menu of the sizes that can be fired AT THIS VENUE. Those are
 * not the same list, because a venue minimum raises anything below it: on
 * Polymarket (5-contract floor) the presets 1, 2 and 3 all submit 5.
 *
 * Rendering one chip per stored preset therefore produced three identical "5"
 * buttons that fire the same order — measured on the real ticket, not
 * inferred. The store dedupes RAW values; nothing deduped the APPLIED ones.
 *
 * So the strip is keyed on the applied quantity: one chip per distinct size a
 * tap can submit, in the order the presets were entered. That keeps the
 * ticket's applied-value contract — the visible label is always the quantity
 * that will be submitted — and it keeps the row honest about how many
 * distinct choices it is really offering. The chip COUNT is consequently
 * venue-dependent, which is the intended reading: the strip describes this
 * venue, not the Settings form.
 *
 * Presets that already clear the floor are untouched, so on Kalshi (floor 1)
 * and for any ordinary Polymarket sizing this collapses nothing at all.
 */

export interface QuickSizeChip {
  /** The quantity a tap submits — already raised to the venue floor. */
  applied: number;
  /** Stored presets that produced it, in entry order. Length > 1 only when a
   *  venue floor collapsed several presets onto the same applied value. */
  sources: number[];
  /** Full `title` text, including the account of any collapse. */
  title: string;
}

/** "3", "1 and 2", "1, 2 and 3" — the presets named in a floor explanation. */
function listPresets(values: readonly number[]): string {
  if (values.length === 1) return String(values[0]);
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

const SETTINGS_TAIL = 'Set in Settings → Preferences';

/**
 * Build the chip strip for one venue.
 *
 * `venueName` and `minQty` describe the floor in the trader's own terms —
 * a chip that silently became 5 has to be able to say WHY, and "Polymarket's
 * 5-contract minimum" is the only wording that distinguishes a floor from a
 * settings wipe. On a venue with no real floor (`minQty <= 1`) nothing is
 * collapsed and no explanation is offered, because there is nothing to explain.
 */
export function buildQuickSizeChips(
  quickSizes: readonly number[],
  minQty: number,
  venueName: string,
): QuickSizeChip[] {
  const byApplied = new Map<number, number[]>();
  for (const preset of quickSizes) {
    const applied = Math.max(preset, minQty);
    const sources = byApplied.get(applied);
    if (sources) sources.push(preset);
    else byApplied.set(applied, [preset]);
  }

  return [...byApplied.entries()].map(([applied, sources]) => {
    const floored = sources.filter((preset) => preset < minQty);
    if (floored.length === 0) {
      return { applied, sources, title: `Set Qty to ${applied} — ${SETTINGS_TAIL}` };
    }
    const noun = floored.length === 1 ? 'quick size' : 'quick sizes';
    const verb = floored.length === 1 ? 'floors' : 'all floor';
    return {
      applied,
      sources,
      title:
        `Set Qty to ${applied} — your ${listPresets(floored)} ${noun} ${verb} to ` +
        `${venueName}'s ${minQty}-contract minimum. ${SETTINGS_TAIL}`,
    };
  });
}
